
import { UserProfile } from '../types';
import { getZodiacSign } from '../utils/dateUtils';

const API_USERS_URL = '/api/users';
const API_LOGIN_URL = '/api/login';
const STORAGE_KEY = 'mystic_user_session';

export const enrichUserWithZodiac = (user: any): UserProfile => {
  let initiation = null;
  if (user && user.initiation) {
    try {
      initiation =
        typeof user.initiation === 'string'
          ? JSON.parse(user.initiation)
          : user.initiation;
    } catch (e) {
      initiation = null;
    }
  }

  return {
    ...user,
    initiation,
    zodiacSign: user.birth ? getZodiacSign(user.birth) : undefined,
  };
};

export const updateLoggedInProfile = async (
  userId: number,
  body: {
    profile?: Partial<
      Pick<UserProfile, 'name' | 'birth' | 'phone' | 'email'>
    > & { avatar?: string | null };
    currentPassword?: string;
    newPassword?: string;
    newPasswordAgain?: string;
  }
): Promise<{ user: UserProfile | null; error?: string }> => {
  try {
    const res = await fetch(API_USERS_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': String(userId),
      },
      body: JSON.stringify({
        loggedInProfileUpdate: true,
        profile: body.profile,
        currentPassword: body.currentPassword,
        newPassword: body.newPassword,
        newPasswordAgain: body.newPasswordAgain,
      }),
    });

    if (res.ok) {
      const raw = await res.json();
      const fullUser = enrichUserWithZodiac(raw);
      saveSessionUser(fullUser);
      return { user: fullUser };
    }

    let errorMessage = 'Nie udało się zapisać.';
    try {
      const errData = await res.json();
      if (errData.error) errorMessage = String(errData.error);
    } catch {
      /* ignore */
    }
    return { user: null, error: errorMessage };
  } catch {
    return { user: null, error: 'Błąd połączenia z serwerem.' };
  }
};

/** Upload awatara na R2 (/esoteric/users/…); zwraca publiczny URL. */
export const uploadUserAvatar = async (userId: number, dataUrl: string): Promise<{ url: string } | { error: string }> => {
  try {
    const res = await fetch('/api/user-avatar', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': String(userId),
      },
      body: JSON.stringify({ image: dataUrl }),
    });
    if (!res.ok) {
      let msg = 'Nie udało się wgrać zdjęcia.';
      try {
        const e = await res.json();
        if (e.error) msg = String(e.error);
      } catch {
        /* ignore */
      }
      return { error: msg };
    }
    const j = await res.json();
    if (typeof j.url === 'string') return { url: j.url };
    return { error: 'Nieprawidłowa odpowiedź serwera.' };
  } catch {
    return { error: 'Błąd połączenia z serwerem.' };
  }
};

export const getUserByPhone = async (phone: string): Promise<UserProfile | null> => {
  try {
    const cleanPhone = phone.replace(/\D/g, '');
    const response = await fetch(`${API_USERS_URL}?phone=${cleanPhone}`, {
      method: 'GET',
    });

    if (response.status === 200) {
      const userData = await response.json();
      return enrichUserWithZodiac(userData);
    }
    
    return null; 
  } catch (error) {
    console.error('Error fetching user:', error);
    return null;
  }
};

export const loginUser = async (identifier: string, password: string): Promise<UserProfile | null> => {
    try {
      const response = await fetch(API_LOGIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim(), password }),
      });

      if (response.ok) {
        const userData = await response.json();
        const fullUser = enrichUserWithZodiac(userData);
        saveSessionUser(fullUser);
        return fullUser;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Login error:', error);
      return null;
    }
  };

export interface RegisterPayload {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  birth?: string | null;
  password: string;
  registrationTicket: string;
}

// Rejestracja teraz wymaga hasła i zwraca obiekt z ewentualnym błędem
export const registerUser = async (
  userData: RegisterPayload | (Omit<UserProfile, 'id' | 'zodiacSign'> & { password: string })
): Promise<{ user: UserProfile | null, error?: string }> => {
  try {
    const response = await fetch(API_USERS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (response.ok) {
      const createdUser = await response.json();
      const fullUser = enrichUserWithZodiac(createdUser);
      saveSessionUser(fullUser); 
      return { user: fullUser };
    } else {
      let errorMessage = 'Błąd rejestracji.';
      try {
        const errorData = await response.json();
        if (errorData.error) {
            errorMessage = errorData.error;
        }
      } catch (e) {
        // Fallback jeśli odpowiedź nie jest JSONem
        console.error("Non-JSON error response", e);
      }
      return { user: null, error: errorMessage };
    }
  } catch (error) {
    console.error('Error registering user:', error);
    return { user: null, error: 'Błąd połączenia z serwerem.' };
  }
};

/** Uzupełnienie profilu po rejestracji (wymaga ponownego hasła z sesji tworzenia konta). */
export const updateProfileAfterRegistration = async (
  identifier: string,
  password: string,
  profile: { name?: string; birth?: string; birthDate?: string; email?: string; phone?: string; extraPhone?: string }
): Promise<{ user: UserProfile | null; error?: string }> => {
  try {
    const response = await fetch(API_USERS_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        profileSelfUpdate: true,
        identifier: identifier.trim(),
        password,
        profile,
      }),
    });

    if (response.ok) {
      const raw = await response.json();
      const fullUser = enrichUserWithZodiac(raw);
      saveSessionUser(fullUser);
      return { user: fullUser };
    }

    let errorMessage = 'Nie udało się zapisać profilu.';
    try {
      const errData = await response.json();
      if (errData.error) errorMessage = errData.error;
    } catch {
      /* ignore */
    }
    return { user: null, error: errorMessage };
  } catch {
    return { user: null, error: 'Błąd połączenia z serwerem.' };
  }
};

// Reset hasła przez bilet OTP (po weryfikacji kodu)
export const resetPasswordWithOtpTicket = async (registrationTicket: string, newPassword: string): Promise<UserProfile | null> => {
  try {
    const response = await fetch(API_USERS_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ registrationTicket, newPassword }),
    });

    if (response.ok) {
      const userData = await response.json();
      const fullUser = enrichUserWithZodiac(userData);
      saveSessionUser(fullUser);
      return fullUser;
    }
    return null;
  } catch (error) {
    console.error('Reset password ticket error:', error);
    return null;
  }
};

// Reset legacy (bez biletu OTP) po numerze
export const updateUserPassword = async (phone: string, newPassword: string): Promise<UserProfile | null> => {
    try {
      const cleanPhone = phone.replace(/\D/g, '');
      const response = await fetch(API_USERS_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone, newPassword }),
      });
  
      if (response.ok) {
        const userData = await response.json();
        const fullUser = enrichUserWithZodiac(userData);
        saveSessionUser(fullUser);
        return fullUser;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Update password error:', error);
      return null;
    }
};

/** Szkic kreatora logowania/rejestracji w portalu (`AuthPortal`) — czyścić przy wylogowaniu. */
export const PORTAL_REG_STATE_KEY = 'esoteric_reg_state';

/** Nagłówek i inne części UI mogą nasłuchiwać, by odświeżyć stan po zalogowaniu bez przeładowania strony. */
export const USER_SESSION_UPDATED_EVENT = 'esoteric-user-session-updated';

export const saveSessionUser = (user: UserProfile): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(USER_SESSION_UPDATED_EVENT));
  }
};

export const getSessionUser = (): UserProfile | null => {
  const data = localStorage.getItem(STORAGE_KEY);
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

export const logoutUser = (): void => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(PORTAL_REG_STATE_KEY);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(USER_SESSION_UPDATED_EVENT));
  }
};

export const formatPhoneNumberForDisplay = (phone: string): string => {
   const digits = phone.replace(/\D/g, '');
   let coreDigits = digits;
   if (coreDigits.startsWith('48')) coreDigits = coreDigits.substring(2);
   
   if (coreDigits.length === 9) {
       return `+48 ${coreDigits.substring(0, 3)}-${coreDigits.substring(3, 6)}-${coreDigits.substring(6, 9)}`;
   }
   return phone;
};
